import { Component, computed, effect, inject, signal, Signal, WritableSignal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { QuillModule } from 'ngx-quill';
import { ToastrService } from 'ngx-toastr';
import { Attribute } from 'src/app/models/attribute';
import { FolderTrackingCustomization } from 'src/app/models/folder-tracking-customization';
import { FolderTrackingStepCustomization } from 'src/app/models/folder-tracking-step-customization';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { FolderTrackingCustomizationService } from 'src/app/services/folder-tracking-customization.service';
import { MemoryService } from 'src/app/services/memory.service';
import { WorkflowDefinitionsService } from 'src/app/services/workflows/workflow-definitions.service';
import { Step } from '../designer/models/step';
import { AttributesSelectorComponent } from './controls/attributes-selector/attributes-selector.component';
import { StepCustomizationsComponent } from './controls/step-customization/step-customization.component';

/**
 * Composant de personnalisation du suivi de dossier.
 */
@Component({
    selector: 'app-folder-tracking-customization',
    imports: [MatCardModule, ReactiveFormsModule, MatButtonModule, QuillModule, FormsModule, AttributesSelectorComponent, StepCustomizationsComponent],
    templateUrl: './folder-tracking-customization.component.html',
    styleUrl: './folder-tracking-customization.component.scss'
})
export class FolderTrackingCustomizationComponent {
    /**
     * Services utilisés dans le composant.
     */
    private toastrService = inject(ToastrService);

    /**
     * Configuration de la barre d'outils de l'editeur de texte.
     **/
    editorModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'], // boutons de mise en forme
            [{ color: [] }, { background: [] }],
            [{ font: ['Arial', 'Times New Roman', 'Helvetica', 'Verdana', 'Courier New', 'monospace'] }, { size: ['10px', '18px', '32px'] }], // police et taille de la police
            [{ align: [] }, { list: 'bullet' }], // alignement du texte
        ],
    };

    /**
     * Personnalisation des polices de caratères disponibles ainsi que des tailles.
     * L'utilisation de attributors/style permet d'appliquer des styles inline aux elements plutôt que d'utiliser des * classes CSS.
     */
    customOptions = [
        {
            import: 'attributors/style/font',
            whitelist: ['Arial', 'Times New Roman', 'Helvetica', 'Verdana', 'Courier New', 'monospace'],
        },
        {
            import: 'attributors/style/size',
            whitelist: ['10px', '18px', '32px'],
        },
    ];

    /**
     * Titre du suivi de dossier.
     */
    public title: string;

    /**
     * Texte du pied de page.
     */
    public footerText: string;

    /**
     * Texte de l'en-tête.
     */
    public headerText: string;

    /**
     * Personnalisation du suivi de dossier.
     */
    public folderTrackingCustomization: WritableSignal<FolderTrackingCustomization> = signal(null);

    /**
     * Liste des attributs de la vue.
     */
    public attributes: WritableSignal<Attribute[]> = signal([]);

    /**
     * Liste des étapes du workflow.
     */
    public steps: WritableSignal<Step[]> = signal(null);

    /**
     * Personnalisations des étapes du suivi de dossier.
     */
    public stepCustomizations: WritableSignal<FolderTrackingStepCustomization[]> = signal(null);

    /**
     * Attributs à afficher dans l'en-tête.
     */
    public headerAttributes: Signal<Attribute[]> = computed(() => {
        if (this.attributes() && this.folderTrackingCustomization().headerAttributes) {
            return this.attributes().filter((attribute) => this.folderTrackingCustomization().headerAttributes.includes(attribute.id));
        } else {
            return [];
        }
    });

    /**
     * Attributs à afficher dans le pied de page.
     */
    public footerAttributes: Signal<Attribute[]> = computed(() => {
        if (this.attributes() && this.folderTrackingCustomization().footerAttributes) {
            return this.attributes().filter((attribute) => this.folderTrackingCustomization().footerAttributes.includes(attribute.id));
        } else {
            return [];
        }
    });

    /**
     * Constructeur.
     */
    constructor(
        private folderTrackingCustomizationService: FolderTrackingCustomizationService,
        private attributesService: AttributeDefinitionsService,
        public workflowDefinitionsService: WorkflowDefinitionsService,
        public memoryService: MemoryService
    ) {
        effect(
            () => {
                if (this.folderTrackingCustomization() && this.steps()) {
                    this.folderTrackingCustomization().stepCustomizations.forEach((stepCustomization) => {
                        // Alimente le nom de l'étape à partir de la liste des étapes.
                        stepCustomization.stepName = this.steps().find((x) => x.id === stepCustomization.stepId).data.stepName;
                    });
                    this.stepCustomizations.set(this.folderTrackingCustomization().stepCustomizations);
                }
            },
            {
                allowSignalWrites: true,
            }
        );
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        // Charge les données de la personnalisation du suivi de dossier.
        this.folderTrackingCustomizationService.get().subscribe((x) => {
            this.folderTrackingCustomization.set(x);
        });

        // Charge les attributs de la vue.
        this.attributesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let attributes = x.value.sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });
            this.attributes.set(attributes);
        });

        // Charge les étapes du workflow
        this.workflowDefinitionsService.get(this.memoryService.getSelectedView()?.id).subscribe((x) => {
            this.steps.set(x.steps);
        });
    }

    /**
     * Appellé lorsque l'utilisateur sauvegarde le formulaire.
     */
    onSave(): void {
        this.folderTrackingCustomizationService.update(this.folderTrackingCustomization()).subscribe((x) => {
            this.folderTrackingCustomization.set(x);
            this.toastrService.success('Sauvegarde effectuée', '', { enableHtml: true });
        });
    }

    /**
     * Appellé lorsque l'utilisateur ajoute, supprime ou déplace un attributs de l'en-tête.
     * @param attributes : Liste des attributs de l'en-tête.
     */
    public onHeaderAttributesChanged(attributes: Attribute[]) {
        this.folderTrackingCustomization().headerAttributes = attributes.map((x) => x.id);
    }

    /**
     * Appellé lorsque l'utilisateur ajoute, supprime ou déplace un attributs du pied de page.
     * @param attributes : Liste des attributs du pied de page.
     */
    public onFooterAttributesChanged(attributes: Attribute[]) {
        this.folderTrackingCustomization().footerAttributes = attributes.map((x) => x.id);
    }
}
